import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import chevronLeft from "assets/icons/chevron_left-blue.svg";
import chevronRight from "assets/icons/chevron_right-blue.svg";
import { RootState } from "redux/reducers";
import { setSearchPosition } from "redux/actions/report";

type ButtonProps = { readonly disabled: boolean };

const ReportNavigator: React.FC = () => {
  const dispatch = useDispatch();
  const { report } = useSelector((state: RootState) => state);
  // position is 1-indexed to match user display, but scrollPositions is 0-indexed
  const [position, setPosition] = useState<number>(report.search["position"]);

  // update position in local state and redux on search or chapter change
  useEffect(() => {
    setPosition(report.search["position"]);
  }, [report.search]);

  useEffect(() => {
    setPosition(1);
    dispatch(setSearchPosition(1));

    const highlights = document.querySelectorAll<HTMLElement>(".report-highlight");
    if (highlights.length > 0) {
      highlights[0].classList.add("report-highlight-active");
      highlights[0].scrollIntoView({ block: "center" });
    }
  }, [report.currentChapter, dispatch]);

  const scrollToHighlight = (index: number) => {
    const highlights = document.querySelectorAll<HTMLElement>(".report-highlight");
    // Remove active class from any previously active highlight
    highlights.forEach((el) => el.classList.remove("report-highlight-active"));
    const target = highlights[index];
    if (!target) return;
    target.classList.add("report-highlight-active");
    target.scrollIntoView({ block: "center" });
  };

  const prevPosition = () => {
    const newPos = position - 1;
    setPosition(newPos);
    scrollToHighlight(newPos - 1);
    dispatch(setSearchPosition(newPos));
  };

  const nextPosition = () => {
    const newPos = position + 1;
    setPosition(newPos);
    scrollToHighlight(newPos - 1);
    dispatch(setSearchPosition(newPos));
  };

  return (
    <Container>
      <NavigationButton
        disabled={position === 1 || !report.search.scrollPositions.length}
        onClick={() => prevPosition()}
      >
        <img src={chevronLeft} alt="left arrow icon" />
      </NavigationButton>
      <NavInfo>
        <p>
          {report.search.scrollPositions.length ? position : 0} of{" "}
          {report.search.scrollPositions.length}
        </p>
      </NavInfo>
      <NavigationButton
        disabled={position + 1 > report.search.scrollPositions.length}
        onClick={() => nextPosition()}
      >
        <img src={chevronRight} alt="right arrow icon" />
      </NavigationButton>
    </Container>
  );
};

const Container = styled.div`
  background-color: ${(props) => props.theme.colors.lighterGrey};
  border: 1px solid ${(props) => props.theme.colors.idataBlue};
  border-radius: 10rem;
  color: ${(props) => props.theme.colors.idataBlue};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavInfo = styled.div`
  font-size: 2.2rem;
  text-align: center;
  p {
    margin: 0;
  }
`;

const disabledStyles = css`
  opacity: 0.4;
  pointer-events: none;
`;

const NavigationButton = styled.button<ButtonProps>`
  background: transparent;
  border: none;
  padding: 0;
  pointer-events: auto;

  img {
    height: 4.5rem;
  }

  &:hover {
    opacity: 0.8;
  }

  ${(props) => (props.disabled ? disabledStyles : null)}
`;

export default ReportNavigator;
